import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from '@mui/material';
import { Drawdown, RepaymentPreview, RepaymentRequest } from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { useFinancingLookups } from './useFinancingLookups';

const cell = { flex: '1 1 200px', maxWidth: 300 } as const;

const RepaymentDialog: React.FC<{ open: boolean; institutionId: string; drawdown: Drawdown | null; defaultBankId?: string | null;
  currency?: string | null; onClose: () => void; onSaved: () => void }> = ({ open, institutionId, drawdown, defaultBankId, currency, onClose, onSaved }) => {
  const toast = useToast();
  const { bankAccounts } = useFinancingLookups(institutionId);
  const [f, setF] = useState<Record<string, string>>({});
  const [prepay, setPrepay] = useState(false);
  const [preview, setPreview] = useState<RepaymentPreview | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null); setPrepay(false);
    setF({ paymentDate: toLocalIsoDate(), totalAmount: '', foreignPrincipalAmount: '', exchangeRate: '', chargesAmount: '', interestAmount: '', bankAccountId: defaultBankId ?? '', reference: '' });
  }, [open, defaultBankId]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => { setPreview(null); setF(s => ({ ...s, [k]: e.target.value })); };
  const num = (v: string) => (v === '' ? null : Number(v));
  // W3-B4: a foreign-currency drawdown is repaid with the foreign principal and the rate the bank charged;
  // the BDT total is then derived by the server (foreign x rate + interest + charges).
  const foreign = drawdown?.foreignAmount != null;
  const body = (): RepaymentRequest => ({
    drawdownId: drawdown!.id, paymentDate: f.paymentDate, totalAmount: foreign ? null : Number(f.totalAmount),
    foreignPrincipalAmount: foreign ? num(f.foreignPrincipalAmount) : null, exchangeRate: foreign ? num(f.exchangeRate) : null,
    chargesAmount: num(f.chargesAmount),
    interestAmount: num(f.interestAmount), bankAccountId: f.bankAccountId, allowPrepayment: prepay, reference: f.reference || null,
  });
  const valid = !!drawdown && f.paymentDate && f.bankAccountId &&
    (foreign ? Number(f.foreignPrincipalAmount) > 0 && Number(f.exchangeRate) > 0 : Number(f.totalAmount) > 0);

  const run = async (kind: 'preview' | 'save') => {
    setBusy(true);
    try {
      if (kind === 'preview') setPreview(await BorrowingService.previewRepayment(body()));
      else { await BorrowingService.createRepayment(body()); toast.showSuccess('Repayment posted'); onSaved(); }
    } catch (err) {
      toast.showApiError(err, kind === 'preview' ? 'Could not preview the repayment' : 'Could not post the repayment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ 'data-help-page': 'fin.repayment.page' } as any}>
      <DialogTitle>Repay {drawdown?.drawdownNo}</DialogTitle>
      <DialogContent>
        <Box data-help-section="fin.repayment.section.body" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
          <TextField data-help="fin.repayment.field.date" sx={cell} size="small" type="date" label="Payment date" required InputLabelProps={{ shrink: true }} value={f.paymentDate ?? ''} onChange={set('paymentDate')} />
          {foreign ? (
            <>
              <TextField data-help="fin.repayment.field.foreignPrincipal" sx={cell} size="small" type="number" label={`Principal paid (${currency ?? 'foreign'})`} required
                value={f.foreignPrincipalAmount ?? ''} onChange={set('foreignPrincipalAmount')} />
              <TextField data-help="fin.repayment.field.rate" sx={cell} size="small" type="number" label="Rate charged by the bank" required
                value={f.exchangeRate ?? ''} onChange={set('exchangeRate')} />
            </>
          ) : (
            <TextField data-help="fin.repayment.field.total" sx={cell} size="small" type="number" label="Total paid to bank" required value={f.totalAmount ?? ''} onChange={set('totalAmount')} />
          )}
          <TextField data-help="fin.repayment.field.charges" sx={cell} size="small" type="number" label="Charges included" value={f.chargesAmount ?? ''} onChange={set('chargesAmount')} />
          <TextField data-help="fin.repayment.field.interest" sx={cell} size="small" type="number" label="Interest (blank = accrued)" value={f.interestAmount ?? ''} onChange={set('interestAmount')} />
          <Box sx={cell}>
            <SearchableSelect data-help="fin.repayment.field.bank" options={bankAccounts} value={f.bankAccountId ?? ''} onChange={id => { setPreview(null); setF(s => ({ ...s, bankAccountId: id })); }}
              getOptionLabel={b => `${b.bankName} ${b.accountNumber}`} label="Paid from" size="small" required />
          </Box>
          <TextField data-help="fin.repayment.field.reference" sx={cell} size="small" label="Reference" value={f.reference ?? ''} onChange={set('reference')} />
        </Box>
        <FormControlLabel data-help="fin.repayment.field.prepay" control={<Checkbox checked={prepay} onChange={e => { setPreview(null); setPrepay(e.target.checked); }} />}
          label="Apply any excess as a prepayment (rebuilds the remaining schedule)" />
        {preview && (
          <Alert severity="info" data-testid="repayment-preview" sx={{ mt: 1 }}>
            <Typography variant="body2">Interest {formatMoney(preview.interestAmount, currency)} (accrued unpaid {formatMoney(preview.accruedInterestUnpaid, currency)})</Typography>
            <Typography variant="body2">Principal due {formatMoney(preview.principalDueAmount, currency)}</Typography>
            <Typography variant="body2">Prepayment {formatMoney(preview.prepaymentAmount, currency)}</Typography>
            <Typography variant="body2">Outstanding {formatMoney(preview.outstandingBefore, currency)} to {formatMoney(preview.outstandingAfter, currency)}</Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="fin.repayment.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fin.repayment.preview" onClick={() => run('preview')} disabled={!valid || busy}>Preview</Button>
        <Button data-help="fin.repayment.save" variant="contained" onClick={() => run('save')} disabled={!valid || busy}>Post repayment</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RepaymentDialog;
